<template>
  <div class="step-token">
    <div class="ios-group-header">步骤 2 · 校验 Token</div>

    <div class="ios-card card-main">
      <div class="card-icon-row">
        <div class="card-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
          </svg>
        </div>
        <div>
          <div class="card-title">输入 Token</div>
          <div class="card-desc">粘贴 ChatGPT 账号的会话 JSON 数据</div>
        </div>
      </div>

      <div class="ios-callout is-info token-guide">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        <div>
          <div class="ios-callout-title">如何获取 Token 数据？</div>
          <ol class="guide-steps">
            <li>打开 <a href="https://chatgpt.com" target="_blank" rel="noopener noreferrer">chatgpt.com</a> 登录需兑换的 ChatGPT 账号</li>
            <li>访问 <a href="https://chatgpt.com/api/auth/session" target="_blank" rel="noopener noreferrer">chatgpt.com/api/auth/session</a>，复制页面中的全部 JSON 数据</li>
          </ol>
        </div>
      </div>

      <div class="input-group">
        <label class="ios-field-label" for="token">Token 信息</label>
        <textarea
          id="token"
          v-model="token"
          placeholder='例如：{"user":{"email":"test@example.com"}}'
          class="ios-textarea"
          :class="{ 'has-error': error }"
          rows="4"
        ></textarea>
        <div v-if="error" class="ios-error-text">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{{ error }}</span>
        </div>
      </div>

      <button
        @click="validateToken"
        class="btn-filled"
        :disabled="!token.trim() || loading"
      >
        <span v-if="loading" class="ios-spinner"></span>
        {{ loading ? '验证中…' : '校验 Token' }}
      </button>
    </div>

    <template v-if="tokenInfo">
      <div class="ios-group-header">账号信息</div>
      <div class="ios-card ios-list token-info">
        <div class="ios-row">
          <span class="ios-row-label">邮箱</span>
          <span class="ios-row-value">{{ tokenInfo.email }}</span>
        </div>
        <div class="ios-row">
          <span class="ios-row-label">昵称</span>
          <span class="ios-row-value">{{ tokenInfo.name }}</span>
        </div>
        <div class="ios-row">
          <span class="ios-row-label">账号 ID</span>
          <span class="ios-row-value">{{ tokenInfo.accountId }}</span>
        </div>
        <div class="ios-row">
          <span class="ios-row-label">Access Token</span>
          <span class="ios-row-value status-available">已解析</span>
        </div>
        <div v-if="tokenInfo.accountType" class="ios-row">
          <span class="ios-row-label">账号类型</span>
          <span class="ios-row-value">{{ formatAccountType(tokenInfo.accountType) }}</span>
        </div>
        <div v-if="tokenInfo.expires" class="ios-row">
          <span class="ios-row-label">到期时间</span>
          <span class="ios-row-value">{{ tokenInfo.expires }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { parseSessionToken } from '../utils/sessionToken'

export default {
  name: 'StepToken',
  data() {
    return {
      token: '',
      tokenInfo: null,
      error: '',
      loading: false
    }
  },
  methods: {
    validateToken() {
      this.loading = true
      this.error = ''
      this.tokenInfo = null

      const response = parseSessionToken(this.token)

      if (response.ok) {
        this.tokenInfo = response.data
        this.$emit('validated', { token: this.token, tokenInfo: response.data })
      } else {
        this.error = response.message
      }

      this.loading = false
    },
    formatAccountType(type) {
      const types = {
        'prolite': 'Pro Lite',
        'pro': 'Pro',
        'enterprise': '企业版'
      }
      return types[type] || type
    }
  }
}
</script>

<style scoped>
.card-main {
  padding: 26px 22px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.card-icon-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.card-icon {
  width: 44px;
  height: 44px;
  border-radius: 11px;
  background: rgba(175, 82, 222, 0.1);
  color: var(--ios-purple);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-icon svg {
  width: 22px;
  height: 22px;
}

.card-title {
  font-size: 19px;
  font-weight: 600;
}

.card-desc {
  margin-top: 2px;
  font-size: 14px;
  color: var(--ios-label-secondary);
}

.guide-steps {
  margin: 4px 0 0 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.guide-steps a {
  color: var(--ios-blue);
  text-decoration: none;
  font-weight: 500;
  word-break: break-all;
}

.guide-steps a:active {
  opacity: 0.5;
}

.input-group {
  display: flex;
  flex-direction: column;
}

.token-info {
  animation: fadeIn 0.35s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 600px) {
  .card-main {
    padding: 16px 14px;
  }
}
</style>
